/**
 * What the chat does when the pipe breaks, decided without the store.
 *
 * A person who typed is owed an answer. A lost worker heartbeat, a soft
 * limit, and a session that needs a page load are transport problems. None
 * of them should end on a banner that asks them to type the message again.
 */

/** Keep tailing a turn whose worker heartbeat dropped, while the server still says it is running. */
export const WORKER_LOSS_MAX_REATTACHES = 60;
/** Twenty minutes covers a long creative turn without following a stuck "active" flag forever. */
export const WORKER_LOSS_MAX_ELAPSED_MS = 20 * 60 * 1000;
export const WORKER_LOSS_REATTACH_DELAY_MS = 2000;

/**
 * A 409 `conversation_turn_changed` in the few seconds after an answer is the
 * previous turn still releasing its claim. Retry the same send. Never retry
 * `empty_message_no_active_turn` — that refusal is the message, not a race.
 */
export const TURN_CHANGED_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

export function turnChangedRetryDelayMs(
  error: { status?: number; code?: string },
  retriesAlreadyUsed: number,
): number | null {
  if (error.code === 'empty_message_no_active_turn') return null;
  if (error.status !== 409 || error.code !== 'conversation_turn_changed') return null;
  const delay = TURN_CHANGED_RETRY_DELAYS_MS[retriesAlreadyUsed];
  return delay === undefined ? null : delay;
}

/** One automatic rejoin per typed message. The next failure shows the banner. */
export const MAX_AUTO_RESUMES = 1;

/**
 * The status endpoint's verdict about ONE turn.
 *
 * `finished` is `idle`: nothing running and no zombie, so the saved
 * conversation is the source of truth. `stale` is a worker that died without
 * a terminal event — its checkpoint survives, its answer does not exist.
 * `unknown` covers a failed status read and a conversation now running a
 * DIFFERENT turn; following that one would paint another turn's answer under
 * this message.
 */
export type ServerTurnState = 'active' | 'finished' | 'stale' | 'unknown';

export function serverTurnState(
  status: { isActive: boolean; status?: string; turnId?: string },
  turnId: string | null | undefined,
): ServerTurnState {
  if (status.isActive) {
    return turnId && status.turnId && status.turnId !== turnId ? 'unknown' : 'active';
  }
  if (status.status === 'idle') return 'finished';
  if (status.status === 'stale') return 'stale';
  return 'unknown';
}

export type WorkerLossAction = 'reattach' | 'show_saved' | 'banner';

/**
 * After the worker's heartbeat drops: follow the turn, paint what was saved,
 * or ask.
 *
 * Text already on screen is not evidence of an answer — it can be streamed
 * reasoning. Only the server can say the turn finished, so only `finished`
 * paints from the save. A dead or unidentified turn gets the Retry banner,
 * which rejoins its checkpoint, rather than a silent stop.
 */
export function workerLossRecovery(input: {
  turnState: ServerTurnState;
  reattachCount: number;
  elapsedSinceLossMs: number;
}): WorkerLossAction {
  const withinBudget =
    input.reattachCount < WORKER_LOSS_MAX_REATTACHES
    && input.elapsedSinceLossMs < WORKER_LOSS_MAX_ELAPSED_MS;
  if (input.turnState === 'active') return withinBudget ? 'reattach' : 'banner';
  if (input.turnState === 'finished') return 'show_saved';
  return 'banner';
}

const autoResumesBySend = new Map<string, number>();
/** Enough for a long session; the oldest claims age out first. */
const AUTO_RESUME_MEMORY = 500;

/**
 * Claim this message's one automatic rejoin. False once it is spent, and
 * always false without a send id — an unidentified failure is not one this
 * client can prove it has not already retried.
 */
export function claimAutoResume(sendId: string | undefined): boolean {
  if (!sendId) return false;
  const used = autoResumesBySend.get(sendId) ?? 0;
  if (used >= MAX_AUTO_RESUMES) return false;
  autoResumesBySend.set(sendId, used + 1);
  if (autoResumesBySend.size > AUTO_RESUME_MEMORY) {
    const oldest = autoResumesBySend.keys().next().value;
    if (oldest !== undefined) autoResumesBySend.delete(oldest);
  }
  return true;
}

/**
 * Characters of the reply saved after the person's last message, or null
 * when there is none — how a rehydrate says whether loading the saved
 * conversation recovered the answer. Structured content counts as a reply
 * with no characters.
 */
export function savedReplyChars(
  messages: ReadonlyArray<{ type: string; content: unknown }>,
): number | null {
  let lastUser = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].type === 'user') {
      lastUser = i;
      break;
    }
  }
  if (lastUser < 0) return null;
  let found = false;
  let chars = 0;
  for (const message of messages.slice(lastUser + 1)) {
    if (message.type !== 'assistant') continue;
    if (typeof message.content === 'string') {
      if (message.content.trim()) {
        found = true;
        chars += message.content.length;
      }
    } else if (message.content) {
      found = true;
    }
  }
  return found ? chars : null;
}

/**
 * What the stream knew when it gave up, for the store deciding what to offer.
 *
 * `acknowledged`: the server accepted the send — an event arrived, or this
 * was a read-only tail of an existing turn. Only an unacknowledged send may
 * be parked and replayed after a reload; replaying an accepted one would send
 * the message twice. `postMayHaveLanded`: a POST of it failed with a 502
 * before acknowledgement, so the server may hold it even though no event
 * arrived — it may come back, but not send itself.
 */
export interface DeliveryErrorContext {
  acknowledged: boolean;
  postMayHaveLanded: boolean;
}

const deliveryErrorContexts = new WeakMap<object, DeliveryErrorContext>();

export function markDeliveryError(error: unknown, context: DeliveryErrorContext): void {
  if (typeof error === 'object' && error !== null) deliveryErrorContexts.set(error, context);
}

export function deliveryErrorContext(error: unknown): DeliveryErrorContext | undefined {
  return typeof error === 'object' && error !== null ? deliveryErrorContexts.get(error) : undefined;
}
